import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { CartesianGrid, Line, LineChart, ReferenceLine, Tooltip, XAxis, YAxis } from "recharts";

import type { ColumnDef } from "@tanstack/react-table";

import { ChartFrame, DataTable } from "@/components/data";
import type { DataTablePage } from "@/components/data";
import { FreshnessLine } from "@/components/shell/FreshnessLine";
import { AlertCard } from "@/modules/protex/components/AlertCard";

import { getStage, getStagePeriod, messageFor, stageKey, stagePeriodKey, type StageWeekRow } from "./api";
import { readGrain, readPeriod } from "./components/period";

type Measure = { key: "quality" | "time" | "throughput" | "rework"; label: string; dataKey: string; greenLine: number | null; amberLine: number | null };

/** Which of the four measures reads worst most often, for the headline. */
function worstMeasure(rows: StageWeekRow[]): { key: string; redPeriods: number } | null {
  const counts: Record<string, number> = { quality: 0, time: 0, throughput: 0, rework: 0 };
  for (const row of rows) {
    if (row.quality_rag === "red") counts.quality += 1;
    if (row.time_rag === "red") counts.time += 1;
    if (row.throughput_rag === "red") counts.throughput += 1;
    if (row.rework_rag === "red") counts.rework += 1;
  }
  const [key, redPeriods] = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
  if (redPeriods === 0) return null;
  return { key, redPeriods };
}

const MEASURE_LABEL: Record<string, string> = {
  quality: "quality",
  time: "duration",
  throughput: "throughput",
  rework: "rework rate",
};

export function StageDetail({ areaName }: { areaName: string }) {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const params = useParams({ strict: false }) as { area?: string; module?: string };
  const navigate = useNavigate();
  const grain = readGrain(search);
  const period = readPeriod(search, grain);
  const stage = typeof search.stage === "string" ? search.stage : null;
  const noun = grain === "month" ? "month" : "week";
  const periodCount = grain === "month" ? 6 : 8;

  const weekQuery = useQuery({
    queryKey: stageKey(stage ?? "", period, 8),
    queryFn: () => getStage(stage as string, period ?? undefined, 8),
    enabled: Boolean(stage) && grain === "week",
  });
  const monthQuery = useQuery({
    queryKey: stagePeriodKey(stage ?? "", "month", period, 6),
    queryFn: () => getStagePeriod(stage as string, "month", period ?? undefined, 6),
    enabled: Boolean(stage) && grain === "month",
  });
  const query = grain === "month" ? monthQuery : weekQuery;

  const backToWeek = () => {
    const next = { ...search };
    delete next.stage;
    void navigate({ to: ".", search: next, replace: false });
  };

  const openAccuracyFindings = () => {
    void navigate({
      to: "/app/$area/$module/$tab",
      params: { area: params.area ?? "nucleus", module: params.module ?? "cvce_pipeline", tab: "accuracy_findings" },
    });
  };

  if (!stage) return null;

  if (query.isLoading) {
    return (
      <div className="flex flex-col gap-3" aria-busy="true">
        <p role="status" className="sr-only">
          Loading stage detail…
        </p>
        {[0, 1].map((i) => (
          <div key={i} className="h-40 animate-pulse rounded-ks-md bg-ks-grey-4/40" />
        ))}
      </div>
    );
  }

  if (query.isError || query.data?.data.available === false || !query.data?.data.stage) {
    return (
      <div className="flex flex-col gap-3">
        <BackLink onBack={backToWeek} noun={noun} />
        <AlertCard
          title="We couldn't load this stage"
          detail={query.error ? messageFor(query.error, areaName) : "404: this stage does not exist."}
        />
      </div>
    );
  }

  const data = query.data.data;
  // For grain=month `getStagePeriod` returns its rows under `periods`;
  // `weeks` is the shared field name for grain=week (cvce spec: "for
  // grain=week the existing `weeks` list is still returned unchanged").
  const rows = grain === "month" ? data.periods ?? [] : data.weeks;
  const worst = worstMeasure(rows);
  const stageInfo = data.stage!;
  const t = data.thresholds;

  const measures: Measure[] = [
    { key: "quality", label: "Quality", dataKey: "quality_value", greenLine: t?.quality_green_min ?? null, amberLine: t?.quality_amber_min ?? null },
    { key: "time", label: "Duration (p50, hours)", dataKey: "duration_hours", greenLine: t?.time_green_max_hours ?? null, amberLine: t?.time_amber_max_hours ?? null },
    { key: "throughput", label: "Throughput", dataKey: "throughput", greenLine: null, amberLine: t?.throughput_amber_share ?? null },
    { key: "rework", label: "Rework rate", dataKey: "rework_rate", greenLine: t?.rework_green_max ?? null, amberLine: t?.rework_amber_max ?? null },
  ];

  const chartRows = rows.map((r) => ({
    ...r,
    period_label: r.period_start ?? r.week_start,
    duration_hours: r.duration_p50_ms == null ? null : r.duration_p50_ms / 3_600_000,
  }));

  const page: DataTablePage = { pageIndex: 0, pageSize: rows.length || 1, rowCount: rows.length };
  const columns: ColumnDef<StageWeekRow, unknown>[] = [
    {
      id: "period",
      header: noun === "month" ? "Month" : "Week",
      cell: ({ row }) => row.original.period_start ?? row.original.week_start,
    },
    {
      accessorKey: "n",
      header: "n",
      cell: ({ row }) => (row.original.n === 0 ? "no data" : row.original.n),
    },
    {
      id: "low_sample",
      header: "Sample",
      cell: ({ row }) =>
        row.original.quality_note === "low_rated_sample"
          ? `Only ${row.original.quality_n ?? "few"} rated, too few to judge`
          : row.original.low_sample
            ? "Low sample"
            : "",
    },
    { accessorKey: "overall_rag", header: "Overall" },
  ];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3 rounded-ks-md border border-ks-grey-4 bg-ks-surface px-3 py-2">
        <div className="flex items-center gap-2">
          <BackLink onBack={backToWeek} noun={noun} />
          <span className="text-sm font-semibold text-ks-ink">{stageInfo.label}</span>
        </div>
        <div className="flex items-center gap-2">
          {stageInfo.stage_key === "s14_manual_content_review" && (
            <button
              type="button"
              data-nonmutating
              onClick={openAccuracyFindings}
              className="rounded-ks-sm border border-ks-purple-3 bg-ks-surface px-2 py-1 text-xs text-ks-purple"
            >
              See accuracy findings for this stage
            </button>
          )}
          {data.threshold_status === "provisional" && (
            <span className="rounded-ks-sm bg-ks-warn/10 px-2 py-1 text-xs text-ks-warn">
              Provisional thresholds, reset after 10 reviewed assets.
            </span>
          )}
        </div>
      </div>
      <FreshnessLine meta={query.data.meta} />
      {worst ? (
        <h2 className="text-lg font-semibold text-ks-ink">
          {MEASURE_LABEL[worst.key]} was the worst measure — red in {worst.redPeriods} of {rows.length || periodCount}{" "}
          {noun === "month" ? "months" : "weeks"}.
        </h2>
      ) : (
        <h2 className="text-lg font-semibold text-ks-ink">No red {noun === "month" ? "months" : "weeks"} in this window.</h2>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {measures.map((m) => (
          <ChartFrame key={m.key} title={m.label} titleVisible pointCount={chartRows.length}>
            <LineChart data={chartRows} margin={{ top: 12, right: 16, bottom: 4, left: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-ks-grey-4)" vertical={false} />
              <XAxis dataKey="period_label" stroke="var(--color-ks-grey-1)" />
              <YAxis stroke="var(--color-ks-grey-1)" />
              <Tooltip />
              {m.greenLine != null && <ReferenceLine y={m.greenLine} stroke="var(--color-ks-pass)" strokeDasharray="4 4" />}
              {m.amberLine != null && <ReferenceLine y={m.amberLine} stroke="var(--color-ks-warn)" strokeDasharray="2 4" />}
              <Line type="monotone" dataKey={m.dataKey} stroke="var(--color-ks-ink)" strokeWidth={2} connectNulls={false} isAnimationActive={false} dot />
            </LineChart>
          </ChartFrame>
        ))}
      </div>

      <DataTable columns={columns} rows={rows} page={page} onPageChange={() => {}} />
    </div>
  );
}

function BackLink({ onBack, noun }: { onBack: () => void; noun: "week" | "month" }) {
  return (
    <button
      type="button"
      data-nonmutating
      onClick={onBack}
      className="w-fit text-left text-sm text-ks-ink underline-offset-2 hover:underline"
    >
      ← Back to {noun}
    </button>
  );
}
